import React from 'react'
import styles from './styles.module.scss'

export function InformationSecurityRender() {
  return (
    <div className={styles.termsTemplate}>
      <p className={styles.lastUpdate}>
        Última atualização: 10 de julho de 2026
      </p>

      <div className={styles.space}>
        <h2>1. OBJETIVO E ESCOPO</h2>
        <p>
          A presente Política de Segurança da Informação (PSI) estabelece as
          diretrizes estratégicas e operacionais adotadas pela Resale Tecnologia
          e Serviços Ltda. para a proteção de seus ativos de informação. O
          objetivo principal é assegurar a confidencialidade, integridade,
          disponibilidade, autenticidade e rastreabilidade das informações
          tratadas pela empresa, alinhando-se às melhores práticas de mercado
          (ISO/IEC 27001) e à legislação vigente, com destaque para a Lei Geral
          de Proteção de Dados Pessoais (LGPD – Lei nº 13.709/2018) e requisitos
          de segurança cibernética exigidos em processos licitatórios (Lei nº
          14.133/2021).
        </p>
        <p>
          Esta política aplica-se a todos os colaboradores, sócios, prestadores
          de serviços, parceiros tecnológicos, terceiros autorizados e usuários
          das plataformas da Resale. O escopo abrange todos os ativos
          tecnológicos administrados pela empresa, incluindo informações
          armazenadas ou processadas na plataforma Resale (incluindo Pagimovel®
          e portais white label), ambientes em nuvem (AWS), bancos de dados,
          estações de trabalho, dispositivos móveis corporativos e sistemas
          internos.
        </p>
      </div>

      <div>
        <div className={styles.space}>
          <h2 className={styles.spaceTop}>
            2. PRINCÍPIOS DE SEGURANÇA DA INFORMAÇÃO
          </h2>
          <p>
            A gestão da segurança da informação na Resale fundamenta-se em
            princípios essenciais para garantir a confiabilidade e a
            continuidade das operações. A <strong>Confidencialidade</strong>{' '}
            assegura que a informação seja acessível apenas a pessoas
            autorizadas. A <strong>Integridade</strong> garante a exatidão e a
            completude da informação e de seus métodos de processamento,
            protegendo-a contra modificações não autorizadas.
          </p>
          <p>
            A <strong>Disponibilidade</strong> assegura que a informação e os
            ativos associados estejam acessíveis aos usuários autorizados sempre
            que necessário. A <strong>Autenticidade</strong> garante a
            identidade de um usuário, processo ou dispositivo. A{' '}
            <strong>Rastreabilidade</strong> permite que as ações realizadas em
            um sistema sejam mapeadas inequivocamente para a entidade
            responsável. Por fim, a <strong>Conformidade Legal</strong> assegura
            o alinhamento de todas as práticas com as leis, regulamentações e
            contratos aplicáveis.
          </p>
        </div>

        <div className={styles.space}>
          <h2 className={styles.spaceTop}>3. CLASSIFICAÇÃO DA INFORMAÇÃO</h2>
          <p>
            As informações na Resale são categorizadas e tratadas de acordo com
            seu grau de criticidade e impacto potencial em caso de vazamento,
            alteração ou indisponibilidade. Esta classificação orienta os
            controles de segurança aplicáveis a cada tipo de dado.
          </p>

          <table className={styles.table}>
            <thead>
              <tr>
                <th>Classificação</th>
                <th>Descrição e Nível de Acesso</th>
                <th>Exemplos</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Pública</td>
                <td>
                  Informações que podem ser divulgadas livremente, sem
                  restrições de acesso ou impacto negativo para a empresa.
                </td>
                <td>
                  Materiais institucionais, campanhas públicas, políticas
                  publicadas no site.
                </td>
              </tr>
              <tr>
                <td>Interna</td>
                <td>
                  Informações de uso exclusivo da empresa, acessíveis a
                  colaboradores e parceiros autorizados mediante necessidade de
                  conhecimento.
                </td>
                <td>
                  Processos internos, manuais de procedimentos, indicadores
                  operacionais gerais.
                </td>
              </tr>
              <tr>
                <td>Confidencial</td>
                <td>
                  Informações cujo acesso é estritamente restrito a pessoas
                  autorizadas. O vazamento pode causar danos à empresa, clientes
                  ou terceiros.
                </td>
                <td>
                  Bases de clientes, dados cadastrais, informações comerciais
                  estratégicas, contratos, documentos de imóveis, credenciais de
                  acesso, código-fonte.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className={styles.space}>
          <h2 className={styles.spaceTop}>
            4. CONTROLES DE ACESSO E SEGURANÇA DA INFRAESTRUTURA
          </h2>
          <p>
            O acesso aos sistemas e plataformas da Resale ocorre mediante
            autenticação individual e intransferível. A empresa adota o
            princípio do menor privilégio, garantindo que os usuários tenham
            acesso apenas aos recursos estritamente necessários para o
            desempenho de suas funções. Os controles incluem a identificação
            individual, definição de perfis de acesso baseados em funções
            (RBAC), segregação de permissões, processos formalizados para
            concessão e revogação de acessos, e o registro em log das principais
            operações realizadas. Cada usuário é integralmente responsável pela
            confidencialidade de suas credenciais.
          </p>
          <p>
            A infraestrutura tecnológica da plataforma Resale é hospedada em
            ambientes de computação em nuvem da Amazon Web Services (AWS),
            reconhecida globalmente por seus altos padrões de segurança. A
            arquitetura é desenhada para garantir alta disponibilidade,
            escalabilidade, redundância e proteção robusta contra ameaças
            cibernéticas. Toda a comunicação de dados entre os usuários e a
            plataforma é criptografada utilizando protocolos seguros
            (HTTPS/TLS), protegendo as informações em trânsito contra
            interceptações.
          </p>

          <p className={styles.underline}>
            <strong>Certificações e Conformidades da Infraestrutura AWS</strong>
          </p>
          <p>
            A infraestrutura AWS que hospeda a plataforma Resale é certificada e
            auditada por terceiros independentes, atendendo aos mais rigorosos
            padrões internacionais de segurança e conformidade. As principais
            certificações incluem <strong>ISO/IEC 27001:2022</strong> (Gestão de
            Segurança da Informação), <strong>ISO 27017</strong> (Segurança em
            Computação em Nuvem), <strong>ISO 27018</strong> (Proteção de Dados
            Pessoais na Nuvem), <strong>SOC 1, SOC 2 e SOC 3</strong>{' '}
            (Relatórios de Controles de Serviço),{' '}
            <strong>PCI DSS Level 1</strong> (Segurança de Dados de Pagamento) e{' '}
            <strong>NIST FIPS 140-2</strong> (Criptografia Validada).
          </p>
          <p>
            Além disso, a AWS confirma que todos os seus serviços podem ser
            utilizados em conformidade com a{' '}
            <strong>Lei Geral de Proteção de Dados Pessoais (LGPD)</strong>,
            oferecendo Data Processing Agreement (DPA) que atende aos requisitos
            legais brasileiros. A plataforma Resale, ao utilizar a
            infraestrutura certificada da AWS, herda os benefícios dessas
            certificações e conformidades, garantindo que os dados dos usuários
            e clientes sejam processados e armazenados em um ambiente que atende
            aos mais altos padrões de proteção e segurança reconhecidos
            internacionalmente.
          </p>
        </div>

        <div className={styles.space}>
          <h2 className={styles.spaceTop}>
            5. DESENVOLVIMENTO SEGURO E CONTINUIDADE DE NEGÓCIOS
          </h2>
          <p>
            O desenvolvimento da plataforma é realizado internamente pela equipe
            técnica da Resale, seguindo metodologias ágeis e práticas de
            desenvolvimento seguro (DevSecOps). As evoluções do sistema obedecem
            a processos rigorosos que incluem ambientes segregados para
            desenvolvimento, testes, homologação e produção, além de revisões de
            código e análises de vulnerabilidade, buscando preservar a
            estabilidade da solução e minimizar riscos operacionais.
          </p>
          <p>
            Para garantir a resiliência das operações, a Resale mantém políticas
            de backup e planos de continuidade de negócios. São realizadas
            rotinas automatizadas de backup utilizando os recursos da AWS,
            possibilitando a recuperação íntegra das informações em caso de
            falhas ou necessidade de restauração. A infraestrutura em nuvem
            oferece mecanismos de redundância que suportam a rápida recuperação
            de desastres (Disaster Recovery), aliados a procedimentos
            operacionais internos desenhados para restabelecer os serviços no
            menor prazo possível em cenários de indisponibilidade.
          </p>
        </div>

        <div className={styles.space}>
          <h2 className={styles.spaceTop}>
            6. PROTEÇÃO DE DADOS PESSOAIS E PRIVACIDADE
          </h2>
          <p>
            A Resale tem o compromisso de proteger a privacidade e os dados
            pessoais de seus usuários, clientes e parceiros, realizando o
            tratamento de dados em estrita conformidade com a Lei Geral de
            Proteção de Dados Pessoais (LGPD). A coleta, o uso, o armazenamento
            e o compartilhamento de dados pessoais são regidos pelas bases
            legais previstas na legislação, garantindo a transparência e os
            direitos dos titulares.
          </p>
          <p>
            Esta Política de Segurança da Informação é complementada por
            documentos específicos que detalham as práticas de privacidade da
            empresa, os quais integram o arcabouço normativo da Resale:
          </p>

          <ul>
            <li>
              <strong>Política de Privacidade:</strong> Detalha as finalidades
              da coleta de dados, o compartilhamento com parceiros essenciais à
              operação (vendedores, leiloeiros, serviços de infraestrutura) e as
              medidas de segurança adotadas.
            </li>
            <li>
              <strong>Política de Cookies:</strong> Explica a utilização de
              cookies (estritamente necessários, de desempenho, funcionalidade e
              marketing) para melhorar a experiência e a segurança na
              plataforma.
            </li>
            <li>
              <strong>Política de Exclusão de Dados:</strong> Estabelece os
              procedimentos e prazos (até 15 dias) para o exercício do direito
              de exclusão pelo titular, ressalvadas as limitações legais para
              retenção de dados (como obrigações fiscais ou prevenção a
              fraudes).
            </li>
          </ul>
          <p>
            Para assuntos relacionados à proteção de dados pessoais, o titular
            pode contatar o Encarregado pelo Tratamento de Dados Pessoais (DPO)
            através do e-mail:{' '}
            <a href="mailto:dpo@resale.com.br" rel="noreferrer">
              dpo@resale.com.br
            </a>
            .
          </p>

          <p className={styles.underline}>
            <strong>Modelo de Responsabilidade Compartilhada</strong>
          </p>
          <p>
            A Resale adota o modelo de responsabilidade compartilhada (Shared
            Responsibility Model) com a AWS. Neste modelo, a{' '}
            <strong>
              AWS é responsável pela segurança da infraestrutura em nuvem
            </strong>
            , incluindo hardware, software de infraestrutura, networking e data
            centers. A{' '}
            <strong>
              Resale é responsável pela segurança dos dados e aplicações
            </strong>{' '}
            que utiliza na nuvem, incluindo a implementação de controles de
            acesso, criptografia de conteúdo, gerenciamento de credenciais e
            políticas de segurança específicas para seus usuários e clientes.
            Esta divisão clara de responsabilidades garante que ambas as partes
            contribuam de forma integrada para a proteção completa das
            informações.
          </p>
        </div>

        <div className={styles.space}>
          <h2 className={styles.spaceTop}>
            7. GESTÃO DE INCIDENTES E RESPONSABILIDADES
          </h2>
          <p>
            A rápida detecção e resposta a incidentes de segurança é fundamental
            para minimizar impactos. Qualquer colaborador, parceiro ou usuário
            que identifique um incidente ou vulnerabilidade envolvendo a
            segurança da informação deve comunicar imediatamente a área de
            Tecnologia da Resale. Os incidentes são classificados, avaliados
            quanto ao seu impacto e tratados conforme os procedimentos internos
            de resposta a incidentes, acionando as medidas técnicas, legais e
            regulatórias cabíveis, incluindo a notificação à Autoridade Nacional
            de Proteção de Dados (ANPD) quando aplicável.
          </p>
          <p>
            A governança da segurança da informação distribui responsabilidades
            claras entre as áreas da empresa. A <strong>Diretoria</strong> é
            responsável por aprovar esta política, prover os recursos
            necessários e promover a cultura de segurança da informação. A área
            de <strong>Tecnologia</strong> é encarregada de administrar a
            infraestrutura, implementar e manter os controles técnicos de
            segurança, e promover melhorias contínuas. Todos os{' '}
            <strong>Colaboradores e Terceiros</strong> têm o dever de cumprir as
            diretrizes desta política, zelar pela confidencialidade das
            informações e reportar prontamente qualquer anomalia ou incidente de
            segurança.
          </p>
        </div>

        <div className={styles.space}>
          <h2 className={styles.spaceTop}>8. REVISÃO E APROVAÇÃO</h2>
          <p>
            Esta Política de Segurança da Informação é um documento vivo e será
            revisada sempre que ocorrerem alterações relevantes na
            infraestrutura tecnológica, nos processos internos de negócio ou na
            legislação aplicável. Independentemente de eventos motivadores, o
            documento passará por uma revisão periódica, no mínimo, a cada 24
            meses.
          </p>
          <br />
          <br />
          <p>
            <strong>CNPJ:</strong> 21.129.873/0001-79
          </p>
          <p>
            <strong>Encarregado de Dados Pessoais (DPO):</strong>{' '}
            <a href="mailto:dpo@resale.com.br" rel="noreferrer">
              dpo@resale.com.br
            </a>
          </p>
        </div>
      </div>

      <p className={styles.version}>DOR124 - v1.0 - 20260710</p>
    </div>
  )
}
